import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useCustomHotkeys } from '../../../../common/hooks/useCustomHotkeys';
import { IOverlayLoaderStore } from '../../../../modules/common/OverlayLoader.store';
import { EditorMode } from '../../../../modules/editor/models/EditorModes';
import { AbandonDraftConfirmationModal } from '../../../components/AbandonDraftConfirmationModal';
import { AnnotationConfirmationModal, AnnotationConfirmationModalMode } from '../../../components/AnnotationConfirmationModal';
import { SaveBeforeAbandonDraftConfirmationModal } from '../../../components/SaveBeforeAbandonDraftConfirmationModal';
import { SaveBeforeSkipConfirmationModal } from '../../../components/SaveBeforeSkipConfirmationModal';
import { doNothing, doNothingAsync } from '../../../helpers/function.helpers';
import { useLoader } from '../../../helpers/useLoader';
import { Home } from '../../../routes/config/Home';
import { useAnnotationPermissions } from '../annotation.permissions';
import { IAnnotationCreationService } from '../annotationCreation.service';
import { AnnotationStatus } from '../annotations.interface';
import { IAnnotationsStore } from '../annotations.store';
import { AnnotationNavigation } from '../components/AnnotationNavigation';
import { IFreeDrawSegmentationService } from '../freeDrawSegmentation.service';
import { IUndoRedoHistory } from '../undoRedoHistory.service';

export const CreateControlsContainer = observer((): ReactElement => {
  const params = useParams<{ workspaceId: string }>();
  const history = useHistory();
  const annotationCreationService = useInjection(IAnnotationCreationService.$);
  const freeDrawSegmentationService = useInjection(IFreeDrawSegmentationService.$);
  const historyService = useInjection(IUndoRedoHistory.$);
  const overlayLoaderStore = useInjection(IOverlayLoaderStore.$);
  const annotationsStore = useInjection(IAnnotationsStore.$);
  const permissions = useAnnotationPermissions();

  const [showAnnotationConfirmationModal, setShowAnnotationConfirmationModal] = useState(false);
  const [showAbandonConfirmationModal, setShowAbandonConfirmationModal] = useState(false);
  const [showSaveBeforeAbandonConfirmationModal, setShowSaveBeforeAbandonConfirmationModal] = useState(false);
  const [showDiscardConfirmationModal, setShowDiscardConfirmationModal] = useState(false);
  const [showSaveBeforeSkipModal, setShowSaveBeforeSkipModal] = useState(false);
  const { isLoading, withLoaderAsync } = useLoader();

  const handleSubmit = async (): Promise<void> => {
    if (annotationsStore.annotationStatus === AnnotationStatus.REJECTED) {
      await withLoaderAsync(async () => annotationCreationService.batchCorrectAndSubmitAnnotationForReviewAsync(), 'submit-annotation-button');
    } else {
      await withLoaderAsync(async () => annotationCreationService.batchSubmitAnnotationForReviewAsync(), 'submit-annotation-button');
    }
    setShowAnnotationConfirmationModal(false);
  };

  const handleSave = async (): Promise<void> => {
    setShowAnnotationConfirmationModal(false);
    await withLoaderAsync(async () => annotationCreationService.batchCorrectAnnotationAsync(), 'save-annotation-button');
    history.push(Home.Projects.List.All.withParams({ workspaceId: params.workspaceId }));
  };

  const handleSaveAsDraftAsync = async (): Promise<void> => {
    setShowAnnotationConfirmationModal(false);
    await withLoaderAsync(async () => annotationCreationService.batchSaveAnnotationDraftAsync(), 'save-draft-button');
    history.push(Home.Projects.List.All.withParams({ workspaceId: params.workspaceId }));
  };

  const handleAbandonDraftConfirmedAsync = async (): Promise<void> => {
    setShowAbandonConfirmationModal(false);

    if (annotationsStore.image === undefined || !historyService.canUndo) {
      await withLoaderAsync(async () => annotationCreationService.batchAbandonAnnotationDraftAsync(false), 'abandon-annotation-button');
    } else {
      setShowSaveBeforeAbandonConfirmationModal(true);
    }
  };

  const handleSaveBeforeAbandonConfirmedAsync = async (): Promise<void> => {
    setShowSaveBeforeAbandonConfirmationModal(false);
    await withLoaderAsync(async () => annotationCreationService.batchAbandonAnnotationDraftAsync(true), 'abandon-annotation-button');
  };

  const handleAbandonDraftCanceledAsync = (): void => {
    setShowAbandonConfirmationModal(false);
  };

  const handleSaveBeforeAbandonDraftDeclinedAsync = async (): Promise<void> => {
    setShowSaveBeforeAbandonConfirmationModal(false);
    await withLoaderAsync(async () => annotationCreationService.batchAbandonAnnotationDraftAsync(false), 'abandon-annotation-button');
    history.push(Home.Projects.List.All.withParams({ workspaceId: params.workspaceId }));
  };

  const handleSaveBeforeAbandonDraftCanceled = (): void => {
    setShowSaveBeforeAbandonConfirmationModal(false);
  };

  const handleConfirmAbandonDraft = (): void => {
    setShowAbandonConfirmationModal(true);
  };

  const handleDiscardConfirmed = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    setShowDiscardConfirmationModal(false);
    await withLoaderAsync(async () => annotationCreationService.batchDiscardAnnotationDuringAnnotationAsync(), 'discard-annotation-button');
  };

  const handleDiscardCanceled = (): void => {
    setShowDiscardConfirmationModal(false);
  };

  const handleDiscard = (): void => {
    setShowDiscardConfirmationModal(true);
  };

  useEffect(() => {
    return (): void => {
      void annotationCreationService.unlockImagesAsync();
    };
  }, []);

  const toggleAnnotationConfirmationModal = (): void => {
    setShowAnnotationConfirmationModal((prevState) => !prevState);
  };

  const handleSkip = async (): Promise<void> => {
    if (annotationsStore.image === undefined || !historyService.canUndo) {
      await withLoaderAsync(async () => annotationCreationService.skipAnnotationsAsync(), 'skip-annotation-button');
    } else {
      setShowSaveBeforeSkipModal(true);
    }
  };

  const handleSaveBeforeSkipConfirmed = async (): Promise<void> => {
    setShowSaveBeforeSkipModal(false);
    await withLoaderAsync(async (): Promise<void> => {
      await annotationCreationService.batchSaveAnnotationDraftAsync();
      await annotationCreationService.skipAnnotationsAsync();
    }, 'skip-annotation-button');
  };

  const handleSaveBeforeSkipDecline = async (): Promise<void> => {
    setShowSaveBeforeSkipModal(false);
    await withLoaderAsync(async () => annotationCreationService.skipAnnotationsAsync(), 'skip-annotation-button');
  };

  const handleSaveBeforeSkipCancel = (): void => {
    setShowSaveBeforeSkipModal(false);
  };

  const disabled =
    annotationsStore.image === undefined ||
    freeDrawSegmentationService.freeDrawInProgress ||
    freeDrawSegmentationService.freeDrawFeature !== undefined ||
    overlayLoaderStore.isOverlayVisible();

  const isAssignedToCurrentUser = annotationsStore.image?.annotations?.isAssignedToCurrentUser ?? false;
  const status = annotationsStore.annotationStatus;
  const isRejected = status === AnnotationStatus.REJECTED;

  const canDiscard = (status === AnnotationStatus.DRAFT || isRejected) && permissions.canDiscard().allowed;

  useCustomHotkeys(
    'ctrl+shift+enter',
    () => {
      toggleAnnotationConfirmationModal();
    },
    { enabled: !disabled },
    [toggleAnnotationConfirmationModal]
  );

  return (
    <>
      <AnnotationNavigation
        canSave={isRejected}
        canSubmit={true}
        canDiscard={canDiscard}
        hideDraft={isRejected}
        disabled={disabled}
        editorMode={EditorMode.BATCH_ANNOTATION}
        onSubmit={handleSubmit}
        onSave={handleSave}
        onSaveAsDraft={handleSaveAsDraftAsync}
        onSkip={handleSkip}
        hideSkip={isAssignedToCurrentUser || isRejected}
        hideAbandonDraft={!isAssignedToCurrentUser || isRejected}
        onAbandonDraft={handleConfirmAbandonDraft}
        showDiscardModal={showDiscardConfirmationModal}
        onDiscardCanceled={handleDiscardCanceled}
        onDiscardConfirmed={handleDiscardConfirmed}
        onDiscard={handleDiscard}
        canCancel={false}
        showCancelModal={false}
        onCancel={doNothingAsync}
        onCancelCancel={doNothing}
        onConfirmCancel={doNothingAsync}
      />
      <AbandonDraftConfirmationModal
        showAbandonModal={showAbandonConfirmationModal}
        onAbandonConfirm={handleAbandonDraftConfirmedAsync}
        onAbandonCancel={handleAbandonDraftCanceledAsync}
      />
      <SaveBeforeAbandonDraftConfirmationModal
        showSaveBeforeAbandonModal={showSaveBeforeAbandonConfirmationModal}
        onSaveBeforeAbandonCancel={handleSaveBeforeAbandonDraftCanceled}
        onSaveBeforeAbandonConfirm={handleSaveBeforeAbandonConfirmedAsync}
        onSaveBeforeAbandonDecline={handleSaveBeforeAbandonDraftDeclinedAsync}
      />
      <SaveBeforeSkipConfirmationModal
        showSaveBeforeSkipModal={showSaveBeforeSkipModal}
        onSaveBeforeSkipDecline={handleSaveBeforeSkipDecline}
        onSaveBeforeSkipConfirm={handleSaveBeforeSkipConfirmed}
        onSaveBeforeSkipCancel={handleSaveBeforeSkipCancel}
      />
      <AnnotationConfirmationModal
        showAnnotationConfirmationModal={showAnnotationConfirmationModal}
        annotationConfirmationModalMode={AnnotationConfirmationModalMode.SUBMIT}
        onAnnotationConfirm={handleSubmit}
        onAnnotationCancel={toggleAnnotationConfirmationModal}
        rejectAutoCompleteOptions={[]}
      />
    </>
  );
});
